import {
  type Dispatch,
  type PropsWithChildren,
  type RefObject,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import type { Place } from "src/api/SearchResponse";
import type { Geocoded } from "src/GeocodedPlace";
import { addDays } from "src/utils/dateTime";
import { useDateURL } from "src/utils/hooks/useDateURL";
import { useHotelDestinationFromUrl } from "src/utils/hooks/useHotelDestinationFromUrl";
import type { SearchPlace } from "src/utils/hooks/useSearch";
import {
  DEFAULT_MAP_RADIUS,
  DEFAULT_MAP_ZOOM,
} from "../../components/Map/constants";
import {
  type HotelsFilterAction,
  type HotelsFilterState,
  defaultFilterState,
  filtersReducer,
} from "./filtersReducer";
import { DEFAULT_ROOM } from "./HotelSearch/constants";

export type DateCombos = [Date, Date] | undefined;
export type Child = {
  age?: number;
};

export type RoomDetail = {
  adults: number;
  children: Child[];
};

type TargetHotelPin = {
  hoveredId?: string;
  focusedId?: string;
};

type HotelListQueryParams = {
  // Note: When undefined, the destination lat/lng are used
  lat: number | undefined;
  lng: number | undefined;
  enabled: boolean;
  zoom: number;
  radius: number;
  arrivalDate?: Date;
  departureDate?: Date;
  roomDetails: RoomDetail[];
  filters?: HotelsFilterState;
};

// Default Dates
const DEFAULT_STAY_LENGTH = 1;

export type HotelsContextProps = {
  destination?: SearchPlace | Place | Geocoded | undefined;
  stayDatesUI: DateCombos;
  setStayDatesUI: Dispatch<SetStateAction<DateCombos>>;
  roomDetailsUI: RoomDetail[];
  setRoomDetailsUI: Dispatch<SetStateAction<RoomDetail[]>>;
  showMap: boolean;
  setShowMap: Dispatch<SetStateAction<boolean>>;
  targetPinHotelId: TargetHotelPin;
  setTargetPinHotelId: Dispatch<SetStateAction<TargetHotelPin>>;
  hotelListQueryParams: HotelListQueryParams;
  setHotelListQueryParams: Dispatch<SetStateAction<HotelListQueryParams>>;
  sortOption: HotelSortOption;
  setSortOption: Dispatch<HotelSortOption>;
  filtersState: HotelsFilterState;
  dispatchFilters: Dispatch<HotelsFilterAction>;
  // route is from transport destination -> hotel.
  focusedRouteIndex: number | undefined;
  setFocusedRouteIndex: Dispatch<SetStateAction<number | undefined>>;
  isFakeHotelLoad: boolean;
  initiateFakeHotelLoad: () => void;
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: Dispatch<SetStateAction<boolean>>;
  datePickerRef: RefObject<HTMLButtonElement | null>;
  setFocusToDatePicker: () => void;
  showDatesPopover: boolean;
  setShowDatesPopover: Dispatch<SetStateAction<boolean>>;
};

const HotelsContext = createContext<HotelsContextProps | undefined>(undefined);

export function HotelsProvider(
  props: PropsWithChildren<{ override?: Partial<HotelsContextProps> }>,
) {
  // TODO: Eventually all information for getting the destination *should* be in the URL and useSearch(). Currently, we also have
  // to rely on Return Flow State, which tells us whether to swap origin and destination.
  const destination = useHotelDestinationFromUrl();
  const defaultDates: DateCombos = useUrlParamDates();

  const [filtersState, dispatchFilters] = useReducer(
    filtersReducer,
    defaultFilterState,
  );

  // Date range for hotel booking availability search.
  // Note: undefined means that the ui should fallback to static prices
  const [stayDatesUI, setStayDatesUI] = useState<DateCombos>();

  // Details for add/remove room/guest details of a hotel booking availability search.
  const [roomDetailsUI, setRoomDetailsUI] = useState<RoomDetail[]>([
    DEFAULT_ROOM,
  ]);

  // This is used on mobile to indicate whether the map is currently being displayed.
  const [showMap, setShowMap] = useState<boolean>(false);
  const [targetPinHotelId, setTargetPinHotelId] = useState<TargetHotelPin>({
    hoveredId: undefined,
    focusedId: undefined,
  });

  /** The arrivalDate, departureDate and roomDetails within hotelListQueryParams
   * are a snapshot of what the stayDates and roomDetails state were
   * when the search button was pressed */
  const defaultHotelListQueryParams = {
    lat: undefined,
    lng: undefined,
    enabled: true,
    zoom: DEFAULT_MAP_ZOOM,
    radius: DEFAULT_MAP_RADIUS,
    roomDetails: [DEFAULT_ROOM],
    filters: defaultFilterState,
    facilityCodes: [],
    hotelTypeCodes: [],
  };
  const [hotelListQueryParams, setHotelListQueryParams] =
    useState<HotelListQueryParams>(defaultHotelListQueryParams);

  // When the destination changes, reset map-related params
  // Note: Ideally this should not be an effect and should be done at the source when the destination is changed.
  useEffect(() => {
    setHotelListQueryParams((state) => ({
      ...state,
      lat: undefined,
      lng: undefined,
      zoom: DEFAULT_MAP_ZOOM,
    }));
  }, [destination?.lat, destination?.lng, destination?.canonicalName]);

  const [sortOption, setSortOption] = useState<HotelSortOption>("default");

  const [focusedRouteIndex, setFocusedRouteIndex] = useState<number>();

  const [isFakeHotelLoad, setIsFakeHotelLoad] = useState(false);
  const initiateFakeHotelLoad = () => {
    setIsFakeHotelLoad(true);
    setTimeout(() => {
      setIsFakeHotelLoad(false);
    }, 500);
  };

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef<HTMLButtonElement>(null);

  const [showDatesPopover, setShowDatesPopover] = useState(false);

  const setFocusToDatePicker = () => {
    if (datePickerRef.current) {
      setIsDatePickerOpen(true);
      datePickerRef.current.focus();
    }
  };

  // Use default dates in the URL if stayDatesUI has not yet been set.
  const stayDatesUIWithDatesFallback = stayDatesUI ?? defaultDates;
  const hotelListQueryParamsWithDatesFallback = {
    ...hotelListQueryParams,
    arrivalDate: hotelListQueryParams.arrivalDate ?? defaultDates?.[0],
    departureDate: hotelListQueryParams.departureDate ?? defaultDates?.[1],
  };

  return (
    <HotelsContext
      value={{
        destination,
        stayDatesUI: stayDatesUIWithDatesFallback,
        setStayDatesUI,
        roomDetailsUI,
        setRoomDetailsUI,
        showMap,
        setShowMap,
        targetPinHotelId,
        setTargetPinHotelId,
        hotelListQueryParams: hotelListQueryParamsWithDatesFallback,
        setHotelListQueryParams,
        sortOption,
        setSortOption,
        filtersState,
        dispatchFilters,
        focusedRouteIndex,
        setFocusedRouteIndex,
        isFakeHotelLoad,
        initiateFakeHotelLoad,
        isDatePickerOpen,
        setIsDatePickerOpen,
        datePickerRef,
        setFocusToDatePicker,
        showDatesPopover,
        setShowDatesPopover,
        ...props.override,
      }}
      {...props}
    />
  );
}

export function useHotelsContext() {
  const context = useContext(HotelsContext);
  if (!context) {
    throw new Error(`useHotelsContext must be used within HotelsProvider`);
  }
  return context;
}

export function getDefaultCheckInDate() {
  const tonight = new Date();
  tonight.setHours(0, 0, 0, 0);
  return tonight;
}

export function getDefaultCheckOutDate(checkInDate: Date) {
  return addDays(checkInDate, DEFAULT_STAY_LENGTH);
}

function useUrlParamDates(): DateCombos {
  const { getGlobalDates } = useDateURL();

  const { departureDate, returnDate } = getGlobalDates();

  const urlHotelCheckInDate = departureDate;
  urlHotelCheckInDate?.setHours(0, 0, 0, 0);

  if (!urlHotelCheckInDate) {
    return undefined;
  }

  const urlHotelCheckOutDate = returnDate;
  urlHotelCheckOutDate?.setHours(23, 59, 59, 0);

  if (urlHotelCheckOutDate && urlHotelCheckOutDate > urlHotelCheckInDate) {
    return [urlHotelCheckInDate, urlHotelCheckOutDate];
  } else {
    return [
      urlHotelCheckInDate,
      addDays(urlHotelCheckInDate, DEFAULT_STAY_LENGTH),
    ];
  }
}
export type HotelSortOption =
  | "priceLow"
  | "priceHigh"
  | "reviewHigh"
  | "default";
